import { SoapUtils } from './SoapUtils';
import { GetFormPanelRequest } from '../services/soap/features/requests/GetFormPanelRequest';
import { ListFormsRequest } from '../services/soap/form/requests/ListFormsRequest';
import { ItemValue, UpdateItemValueRequest } from '../services/soap/form/requests/UpdateItemValueRequest';

/** Same CLASS constants as FormControl.tsx to avoid importing from a React component. */
const CONTROLS = {
  com: {
    atollon: {
      formDesigner: {
        components: {
          inputs: {
            FormTextInput: 'com.atollon.formDesigner.components.inputs::FormTextInput',
            FormTextArea: 'com.atollon.formDesigner.components.inputs::FormTextArea',
            FormDateField: 'com.atollon.formDesigner.components.inputs::FormDateField',
            FormCheckbox: 'com.atollon.formDesigner.components.inputs::FormCheckbox',
          },
        },
      },
      gui: {
        FolderSelector: 'com.atollon.gui::FolderSelector',
        controls: {
          ItemSelectorField: 'com.atollon.gui.controls::ItemSelectorField',
          UserSelector: 'com.atollon.gui.controls::UserSelector',
        },
      },
    },
  },
};

export interface FormInfo {
  formId: number;
  formIdMode: string;
}

export interface FormInitializationServices {
  featureService: {
    getFormPanel(request: { toXml?: () => string }): Promise<{ count: number; ROW?: { SCHEME?: string } }>;
  };
  formService: {
    listForms(request: { toXml?: () => string }): Promise<{ count: number; ROW?: { definition?: string } }>;
    updateItemValue(request: UpdateItemValueRequest): Promise<{ EXCEPTION?: { message: string } }>;
  };
  store: { Server: string; SessionId: string };
}

export function extractFormViewInfo(
  layoutObject: any,
  formInfos: FormInfo[]
): FormInfo[] {
  if (layoutObject?.viewComponent === 'com.atollon.FormView') {
    formInfos.push({
      formId: layoutObject.componentAttributes.formId,
      formIdMode: layoutObject.componentAttributes.formIdMode,
    });
  }
  if (layoutObject?.children?.length > 0) {
    layoutObject.children.forEach((child: any) => {
      extractFormViewInfo(child, formInfos);
    });
  }
  return formInfos;
}

export function extractDefaultValues(
  componentObject: any,
  defaultValues: Record<string, any>
): Record<string, any> {
  if (!componentObject) return defaultValues;

  if (
    componentObject.CHILDREN?.COMPONENT?.length > 0
  ) {
    componentObject.CHILDREN.COMPONENT.forEach((child: any) => {
      defaultValues = { ...defaultValues, ...extractDefaultValues(child, defaultValues) };
    });
  }

  if (componentObject.FORMITEM) {
    const item_name = `CF_${componentObject.FORMITEM}`;
    switch (componentObject.CLASS) {
      case CONTROLS.com.atollon.formDesigner.components.inputs.FormTextInput:
      case CONTROLS.com.atollon.formDesigner.components.inputs.FormTextArea:
        defaultValues[item_name] = componentObject.PROPERTIES?.text ?? '';
        break;
      case CONTROLS.com.atollon.formDesigner.components.inputs.FormDateField:
        defaultValues[item_name] = componentObject.PROPERTIES?.isoValue
          ? SoapUtils.toDateForXml(new Date(componentObject.PROPERTIES.isoValue), true)
          : null;
        break;
      case CONTROLS.com.atollon.formDesigner.components.inputs.FormCheckbox:
        defaultValues[item_name] = componentObject.PROPERTIES?.selected === 'true';
        break;
      case CONTROLS.com.atollon.gui.FolderSelector:
        defaultValues[item_name] = componentObject.PROPERTIES?.selectedFolderId
          ? +componentObject.PROPERTIES.selectedFolderId
          : null;
        break;
      case CONTROLS.com.atollon.gui.controls.ItemSelectorField:
        defaultValues[item_name] = componentObject.PROPERTIES?.valueId ?? null;
        break;
      case CONTROLS.com.atollon.gui.controls.UserSelector:
        defaultValues[item_name] = componentObject.PROPERTIES?.userId ?? null;
        break;
      default:
        defaultValues[item_name] = componentObject.PROPERTIES?.value;
        break;
    }
  }

  return defaultValues;
}

export async function loadFormPanel(
  activityType: number,
  { featureService, store }: FormInitializationServices
): Promise<any> {
  const formPanel = await featureService.getFormPanel(
    new GetFormPanelRequest(store.Server, store.SessionId, '' + activityType)
  );
  if (formPanel.count > 0 && formPanel.ROW?.SCHEME) {
    const scheme = SoapUtils.parseXmlString2(formPanel.ROW.SCHEME);
    const layoutObject = JSON.parse(scheme.ActivityPanelSettings.layoutObject);
    return layoutObject;
  }
  return null;
}

export async function loadForm(
  formId: number,
  _formIdMode: string,
  { formService, store }: FormInitializationServices
): Promise<any> {
  const form = await formService.listForms(
    new ListFormsRequest(store.Server, store.SessionId, '' + formId)
  );
  if (form.count > 0 && form.ROW?.definition) {
    const jsonObject = SoapUtils.parseXmlString2(form.ROW.definition, ['COMPONENT']);
    return jsonObject;
  }
  return null;
}

/**
 * Returns default custom form values for an activity type (and optional context type form).
 * Used by both single-context and bulk-activity creation to initialize CF_* fields.
 */
export async function getActivityCustomFormDefaultValues(
  activityType: number,
  contextTypeFormId: string | undefined | null,
  templateFormId: string | undefined | null,
  services: FormInitializationServices
): Promise<Record<string, any>> {
  const layoutObject = await loadFormPanel(activityType, services);
  const formViewInfos: FormInfo[] = extractFormViewInfo(layoutObject ?? {}, []);

  if (contextTypeFormId) {
    formViewInfos.push({
      formId: +contextTypeFormId,
      formIdMode: 'self',
    });
  }

  if (templateFormId) {
    formViewInfos.push({
      formId: +templateFormId,
      formIdMode: 'self',
    });
  }

  // Remove duplicates from formViewInfos based on formId
  const seenFormIds = new Set<number>();
  const distinctFormViewInfos: FormInfo[] = [];
  for (const info of formViewInfos) {
    if (!seenFormIds.has(info.formId)) {
      seenFormIds.add(info.formId);
      distinctFormViewInfos.push(info);
    }
  }
  // Now use only distinct formViewInfos
  // const formViewInfos = distinctFormViewInfos;
  // let defaultValues: Record<string, any> = {};
  let defaultValues: Record<string, any> = {};
  for (const formViewInfo of distinctFormViewInfos) {
    const form = await loadForm(formViewInfo.formId, formViewInfo.formIdMode, services);
    if (form?.definition?.gui?.COMPONENT?.[0]) {
      const formDefaultValues = extractDefaultValues(form.definition.gui.COMPONENT[0], {});
      defaultValues = { ...defaultValues, ...formDefaultValues };
    }
  }
  return defaultValues;
}

/**
 * Persists custom form values (CF_* keys) to the given formValuesId via UpdateItemValue.
 */
export async function persistCustomFormValues(
  formValuesId: number,
  values: Record<string, any>,
  { formService, store }: FormInitializationServices
): Promise<boolean> {
  const passed_values = Object.keys(values)
    .filter((key) => key.startsWith('CF_'))
    .map((key) => {
      const value = values[key];
      const formItem = key.substring(3);
      return new ItemValue(
        undefined,
        undefined,
        formValuesId,
        +formItem,
        undefined,
        value !== undefined && value !== null ? String(value) : undefined,
        undefined,
        undefined
      );
    });

  if (passed_values.length === 0) return true;

  const save_response = await formService.updateItemValue(
    new UpdateItemValueRequest(store.Server, store.SessionId, passed_values)
  );

  if (save_response.EXCEPTION) {
    return false;
  }
  return true;
}
